import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { getCategories, createCase, updateCase } from '../../api/cases';
import { BASE_URL } from '../../api/inquiries';
import { fetchWithAuth } from '../../api/apiClient';
import { ACCEPT_IMAGE, IMAGE_HINT } from '../../constants/uploads';
import AiAssistantModal from '../../components/admin/ai/AiAssistantModal';
import CaseCategoryManager from '../../components/admin/CaseCategoryManager';

/** AI 보조작가가 제안하고 사람이 고를 필드. 순서가 검토 화면의 순서다. */
const CASE_AI_FIELDS = [
    { key: 'title', label: '제목' },
    { key: 'description', label: '도입 배경 / 문제' },
    { key: 'solution', label: '적용한 솔루션' },
    { key: 'effect', label: '효과' },
    { key: 'examples', label: '적용 범위' },
    { key: 'appliedCompanies', label: '고객사' },
];

const CaseEditor = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);

    // AI 보조작가는 AiAssistantModal 이 자체 상태로 다룬다.
    // 자료 수집·URL 스크랩·프롬프트가 모달 안으로 옮겨가면서
    // 여기 있던 인라인 상태와 핸들러가 전부 사라졌다.
    const [aiModalOpen, setAiModalOpen] = useState(false);
    // 카테고리 관리는 리스트 상단과 편집 화면 두 곳에서 모두 연다 —
    // 솔루션 편집기와 자리를 맞췄다. 한쪽에만 있으면 찾다가 못 찾는다.
    const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
    const [isCropping, setIsCropping] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        categoryCode: '',
        title: '',
        description: '',
        solution: '',
        effect: '',
        examples: '', // Comma separated string for input
        appliedCompanies: '', // Comma separated string
        icon: '🏢',
        images: [], // Array of URLs
        order: 0
    });

    const loadCategories = useCallback(async () => {
        try {
            setCategories(await getCategories());
        } catch (err) {
            // 카테고리를 못 불러와도 편집 자체는 계속할 수 있어야 한다.
            console.error('카테고리 로드 실패', err);
        }
    }, []);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const cats = await getCategories();
                setCategories(cats);

                if (isEditMode) {
                    // Wait, we DO have findOneCase in backend, but api/cases.js missed exporting it?
                    // Actually api/cases.js has getCases(category) but not getCase(id).
                    // I will fetch all and find client side (or add getCaseById later).
                    // Backend has @Get(':id') -> findOneCase.
                    // Let's rely on fetchOne helper inside here or update api.
                    // I'll implement fetchOne locally for now.
                    const response = await fetch(`${BASE_URL}/api/cases/${id}`);
                    if (!response.ok) throw new Error('Fetch failed');
                    const data = await response.json();

                    setFormData({
                        categoryCode: data.category?.code || '',
                        title: data.title,
                        description: data.description,
                        solution: data.solution,
                        effect: data.effect,
                        examples: data.examples ? data.examples.join(', ') : '',
                        appliedCompanies: data.appliedCompanies ? data.appliedCompanies.join(', ') : '',
                        icon: data.icon,
                        images: data.images || [],
                        order: data.order
                    });
                } else if (cats.length > 0) {
                    setFormData(prev => ({ ...prev, categoryCode: cats[0].code }));
                }
            } catch (error) {
                console.error(error);
                alert('데이터 로딩 실패');
                navigate('/admin/cases');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [id, isEditMode, navigate]);

    /**
     * AI 보조작가가 "선택 반영"한 필드만 받는다.
     * 배열 필드(examples / appliedCompanies)는 폼이 콤마 문자열로 다루므로 변환한다.
     */
    const handleAiApply = (patch) => {
        const next = { ...patch };
        ['examples', 'appliedCompanies'].forEach((k) => {
            if (Array.isArray(next[k])) next[k] = next[k].join(', ');
        });
        setFormData(prev => ({ ...prev, ...next }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // -------------------------
    // Drag and Drop Handlers
    const [dragActiveImg, setDragActiveImg] = useState(false);

    const handleDragImg = (e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActiveImg(true);
        else if (e.type === "dragleave") setDragActiveImg(false);
    };

    const handleDropImg = (e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActiveImg(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageUpload({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Start multiple uploads
        const uploadedUrls = [];
        for (const file of files) {
            const uploadData = new FormData();
            uploadData.append('file', file);

            try {
                const res = await fetchWithAuth(`${BASE_URL}/api/upload?folder=tmp`, {
                    method: 'POST',
                    body: uploadData
                });
                if (!res.ok) throw new Error('Upload failed');
                const data = await res.json();
                uploadedUrls.push(data.url);
            } catch (error) {
                console.error("Gallery image upload failed", error);
                alert(`업로드 실패: ${file.name}`);
            }
        }

        // Add to images array all at once to prevent stale state issues
        if (uploadedUrls.length > 0) {
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...uploadedUrls]
            }));
        }
        
        // Reset file input value so same files could be uploaded again if removed
        if (e.target && e.target.value !== undefined) {
            e.target.value = ''; 
        }
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    /**
     * 대표 이미지 지정 = 갤러리의 맨 앞으로 **이동**.
     *
     * 이전 구현은 원본을 그대로 둔 채 앞에 복제본을 끼워 넣어서
     * (주석에 "Copy to the first position without removing the source")
     * 대표로 지정할 때마다 같은 이미지가 하나씩 늘어났다. 중복처럼 보인 게 아니라
     * 실제로 배열에 중복 항목이 생겼고, 그대로 저장돼 공개 페이지에도 두 번 나왔다.
     */
    const setAsFirstImage = (index) => {
        setFormData(prev => {
            if (index === 0 || index >= prev.images.length) return prev;
            const next = [...prev.images];
            const [selected] = next.splice(index, 1);
            return { ...prev, images: [selected, ...next] };
        });
    };

    // handleSmartClipboardPaste 를 제거했다.
    // 클립보드 텍스트를 .txt/.html 파일로 만들어 AI 첨부 파이프라인에 태우던 우회였는데,
    // AI 보조작가 모달이 붙여넣기를 직접 자료로 받으므로 필요가 없어졌다.
    // (게다가 .html 은 서버가 더 이상 받지 않는다 — 저장형 XSS)
    // 갤러리 이미지 붙여넣기는 아래 handleGalleryClipboardPaste 가 계속 담당한다.

    // 갤러리 이미지 일괄 크롭 — AI 보조작가와 무관한 이미지 기능이라 그대로 둔다.
    const handleBatchCropImages = async () => {
        if (formData.images.length === 0) {
             alert('크롭할 이미지가 없습니다.');
             return;
        }
        if (!window.confirm('갤러리의 모든 원본 이미지를 16:9 비율 최적화(Crop) 규격으로 일괄 자릅니다. 계속하시겠습니까?')) return;
        
        setIsCropping(true);
        try {
            const res = await fetchWithAuth(`${BASE_URL}/api/ai/batch-crop-images`, {
                method: 'POST',
                body: JSON.stringify({ imageUrls: formData.images })
            });
            if (!res.ok) throw new Error('Batch crop failed');
            const data = await res.json();
            if (data.success && data.urls) {
                setFormData(prev => ({ ...prev, images: data.urls }));
                alert('모든 이미지가 1200x675 (16:9) 규격으로 찌그러짐 없이 시원하게 잘라/크롭 되었습니다!');
            }
        } catch (error) {
            console.error(error);
            alert('이미지 일괄 처리 중 오류가 발생했습니다.');
        } finally {
            setIsCropping(false);
        }
    };

    const handleGalleryClipboardPaste = async () => {
        try {
            const clipboardItems = await navigator.clipboard.read();
            const imageFiles = [];
            for (const item of clipboardItems) {
                const imageType = item.types.find(type => type.startsWith('image/'));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    imageFiles.push(new File([blob], `clipboard-gallery-${Date.now()}.png`, { type: imageType }));
                }
            }
            if (imageFiles.length > 0) {
                await handleImageUpload({ target: { files: imageFiles } });
            } else {
                alert('클립보드에 이미지가 없습니다.');
            }
        } catch (err) {
            console.error('Gallery clipboard paste failed:', err);
            alert('클립보드 접근 권한이 필요합니다. 브라우저 주소창 좌측 자물쇠 아이콘에서 클립보드를 [허용]해주세요.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prepare payload
        const payload = {
            ...formData,
            examples: formData.examples.split(',').map(s => s.trim()).filter(Boolean),
            appliedCompanies: formData.appliedCompanies.split(',').map(s => s.trim()).filter(Boolean),
            order: parseInt(formData.order) // Ensure number
        };

        try {
            if (isEditMode) {
                await updateCase(id, payload);
                alert('수정되었습니다.');
            } else {
                await createCase(payload);
                alert('생성되었습니다.');
            }
            navigate('/admin/cases');
        } catch (error) {
            console.error(error);
            alert('저장 실패');
        }
    };

    if (loading) return <AdminLayout><div className="p-10 text-center text-gray-400 font-bold animate-pulse">데이터 로딩 중...</div></AdminLayout>;

    return (
        <AdminLayout>
            <div className="flex items-center gap-4 mb-6">
                <button onClick={() => navigate('/admin/cases')} className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors shadow-sm">&larr;</button>
                <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">{isEditMode ? '사례 상세 수정' : '새 구축 사례 등록'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-200 max-w-4xl max-w-[800px] space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="block text-sm font-bold text-gray-700">카테고리</label>
                            <button
                                type="button"
                                onClick={() => setCategoryManagerOpen(true)}
                                className="text-xs font-bold text-gray-500 hover:text-primary-500"
                            >
                                카테고리 관리
                            </button>
                        </div>
                        <select name="categoryCode" value={formData.categoryCode} onChange={handleInputChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm bg-white font-medium">
                            <option value="">카테고리 선택</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.code}>{c.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">로고 아이콘 (이모지 권장)</label>
                        <input name="icon" value={formData.icon} onChange={handleInputChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-medium" />
                    </div>
                </div>

                {/* AI 보조작가 — 솔루션 편집기와 같은 컴포넌트·같은 자리.
                    이전에는 여기에 인라인 섹션이 펼쳐져 있어 폼이 아래로 밀리고,
                    URL 은 1개만 받고 지시는 파일/클립보드로만 줄 수 있었다. */}
                <div className="flex items-center justify-between p-4 rounded-2xl border border-gray-200 bg-gray-50">
                    <div>
                        <p className="text-sm font-bold text-gray-800 m-0">AI 보조작가</p>
                        <p className="text-xs text-gray-500 mt-1">자료(파일·URL·텍스트)를 담고 지시를 주면 초안을 만듭니다. 결과는 항목별로 골라서 반영합니다.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setAiModalOpen(true)}
                        className="shrink-0 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-bold hover:bg-primary-600 transition-colors"
                    >
                        AI 보조작가 열기
                    </button>
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">사례 초안 키워드 힌트 / 통합 테마 타이틀 (Title)</label>
                    <input name="title" value={formData.title} onChange={handleInputChange} required className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-bold text-gray-900 disabled:opacity-50" placeholder="위 첨부 후 빈칸으로 두셔도 자동 수합됩니다." />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">정렬 순서</label>
                    <input type="number" name="order" value={formData.order} onChange={handleInputChange} className="w-32 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-mono text-center" />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">도입 배경 및 설명 (Description)</label>
                    <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} required className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm text-gray-700 leading-relaxed font-medium" />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">해결 방안 및 구축 내용 (Solution)</label>
                    <textarea name="solution" value={formData.solution} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm text-gray-700 leading-relaxed font-medium" />
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-bold text-gray-700">주요 도입 효과 (Effect)</label>
                    <textarea name="effect" value={formData.effect} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm text-gray-700 leading-relaxed font-medium" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">대표 활용처 (쉼표로 구분)</label>
                        <input name="examples" value={formData.examples} onChange={handleInputChange} placeholder="예: 관공서, 대기업 미팅룸, 물류센터" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-medium" />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700">도입 이력 (쉼표로 구분)</label>
                        <input name="appliedCompanies" value={formData.appliedCompanies} onChange={handleInputChange} placeholder="예: 삼성전자, 서울시청, 네이버" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm font-medium" />
                    </div>
                </div>

                {/* Image Upload */}
                <div 
                    className={`space-y-4 p-6 rounded-2xl border border-dashed transition-all duration-200 ${dragActiveImg ? 'bg-emerald-50 border-emerald-400 scale-[1.01]' : 'bg-gray-50 border-gray-300'}`}
                    onDragEnter={handleDragImg} onDragOver={handleDragImg} onDragLeave={handleDragImg} onDrop={handleDropImg}
                >
                    <div className="flex flex-col gap-3 pointer-events-none">
                        <label className="block text-base font-bold text-gray-800 m-0">📸 현장 이미지 및 레퍼런스 첨부 (다중 가능)</label>
                        <div className="flex flex-wrap gap-2 pointer-events-auto">
                            <button type="button" onClick={handleBatchCropImages} disabled={isCropping || formData.images.length === 0} className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold rounded-xl shadow-sm hover:shadow-md transition-transform disabled:opacity-50 border-0 flex-1 sm:flex-none justify-center">
                                {isCropping ? '⏳ 규격 자르기 중...' : '📐 전체 16:9 다단 크롭 (Crop) 맞춤'}
                            </button>
                            <button type="button" onClick={handleGalleryClipboardPaste} disabled={isCropping} className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded shadow-sm hover:bg-gray-50 text-xs font-bold shrink-0 flex items-center justify-center gap-1 transition-colors">
                                📋 붙여넣기
                            </button>
                        </div>
                    </div>

                    <div className="relative mt-2">
                        <div className={`absolute inset-0 flex items-center justify-center border-2 border-dashed rounded-xl pointer-events-none transition-colors ${dragActiveImg ? 'border-emerald-400 bg-emerald-100/30' : 'border-gray-200 bg-white/70'}`}>
                            <span className="text-sm font-bold text-emerald-600/70">여기로 이미지를 드래그 앤 드롭 하세요 (JPG, PNG)</span>
                        </div>
                        <input type="file" multiple onChange={handleImageUpload} accept={ACCEPT_IMAGE} title={IMAGE_HINT} className="block w-full text-transparent file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 cursor-pointer relative z-10 opacity-0 h-16" />
                    </div>
                    
                    {formData.images.length > 0 && (
                        <div className="flex flex-wrap gap-4 mt-4 bg-white p-4 rounded-xl border border-gray-200">
                            {formData.images.map((url, idx) => (
                                <div key={idx} className={`relative w-32 h-32 rounded-xl overflow-hidden border ${idx === 0 ? 'border-4 border-yellow-400' : 'border-gray-200'} group shadow-sm`}>
                                    <img src={url} alt={`Preview ${idx}`} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                    <button type="button" onClick={() => removeImage(idx)} className="absolute top-2 right-2 bg-red-500/90 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm shadow-md z-10" title="삭제">&times;</button>
                                    {idx > 0 && (
                                        <button type="button" onClick={() => setAsFirstImage(idx)} className="absolute bottom-2 left-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-md px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity font-extrabold shadow-md z-10 hover:scale-105" title="대표 이미지로 승격">⭐ 메인 지정</button>
                                    )}
                                    {idx === 0 && (
                                        <span className="absolute top-2 left-2 bg-yellow-400 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm z-10">대표</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 pt-6 border-t border-gray-100">
                    <button type="button" onClick={() => navigate('/admin/cases')} className="flex-1 py-3 bg-white text-gray-700 font-bold border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm">취소</button>
                    <button type="submit" className="flex-1 py-3 bg-gray-800 text-white font-bold rounded-xl hover:bg-gray-900 transition-colors shadow-sm tracking-wide">
                        {isEditMode ? '수정 사항 저장' : '새 구축 사례 발행'}
                    </button>
                </div>
            </form>

            <AiAssistantModal
                isOpen={aiModalOpen}
                onClose={() => setAiModalOpen(false)}
                contentType="case"
                title="AI 보조작가 — 도입사례"
                fields={CASE_AI_FIELDS}
                currentValues={formData}
                onApply={handleAiApply}
            />
        
            <CaseCategoryManager
                isOpen={categoryManagerOpen}
                onClose={() => { setCategoryManagerOpen(false); loadCategories(); }}
            />

        </AdminLayout>
    );
};

export default CaseEditor;
